import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import exportIcon from "../../../assets/icons/upload.svg";
import ColumnCustomizer from "../../../components/ColumnCustomizer";
import Loader from "../../../components/Loader";

import { Link } from "react-router-dom";
import ExportModal from "../../../components/ImpExpModal/ExportModal";
import ContactPagination from "../../../components/Pagination/ContactPagination";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

const MarketMetrics = ({
    isSidebarCollapsed,
    onCallApi,
    exportCsvData,
    selectedData,
    queryParams,
    activeTab,
    advanceFilterData = [],
    from
}) => {
    const [config] = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [exportType, setExportType] = useState("DealMetrics");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemPerPage, setItemPerPage] = useState(10);
    const [paginationData, setPaginationData] = useState({
        totalItems: 0,
        from: 0,
        to: 0,
        totalPages: 0,
    });

    // ==================== COLUMN CUSTOMIZATION ====================
    const allColumns = [
        { key: 'property_name', label: 'Property Name', fixed: true },
        { key: 'lease_type', label: 'Lease Type' },
        { key: 'lease_term', label: 'Lease Term Remaining' },
        { key: 'noi', label: 'NOI' },
        { key: 'contract_price', label: 'Contract Price' },
        { key: 'cap_rate', label: 'Contract Cap Rate' },
        { key: 'asking_percent', label: '% of Asking Price' },
    ];

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem('marketMetricsVisibleColumns');
        return saved
            ? JSON.parse(saved)
            : allColumns.map(col => col.key);
    });
    // ============================================================

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
        }).format(amount || 0);
    };

    const formatPercent = (value, decimals = 2) => {
        return value !== null && value !== undefined
            ? Number(value).toFixed(decimals) + "%"
            : "N/A";
    };

    const formatYears = (years) => {
        return years ? Number(years).toFixed(1) + " yrs" : "N/A";
    };

    // ==================== LEASE TYPE MIX ====================
    const leaseTypeConfig = {
        nnn: { label: "NNN", color: "#15803d" },
        nn: { label: "NN", color: "#4ade80" },
        gross: { label: "Gross", color: "#22c55e" },
        leasehold: { label: "Leasehold", color: "#86efac" },
        ground: { label: "Ground", color: "#eab308" },
        "roof & structure": { label: "Roof & Structure", color: "#64748b" },
    };

    const leaseTypeMix = Object.entries(dashboardData?.market_metrics?.lease_type_mix || {})
        .map(([key, percent]) => ({
            type: leaseTypeConfig[key]?.label || key,
            percent: Number(percent) || 0,
            color: leaseTypeConfig[key]?.color || "#64748b",
            key: key
        }))
        // Removed filter to show 0% values
        .sort((a, b) => b.percent - a.percent);

    const buildQueryParams = () => {
        let params = `page=${currentPage}&per_page=${itemPerPage}`;

        if (activeTab) {
            params += `&active_tab=${activeTab}`;
        }

        // Advance Filters
        const validFilters = advanceFilterData.filter(group =>
            group?.filters && group.filters.some(f => f.field && f.operator)
        );

        if (validFilters.length > 0) {
            const advanceFiltersQuery = encodeURIComponent(JSON.stringify(validFilters));
            params += `&advance_filters=${advanceFiltersQuery}`;
        }

        return params;
    };


    const fetchDashboardData = () => {
        setLoading(true);

        const queryString = buildQueryParams();

        axios
            .get(`${BASE_URL}/market-metrics?${queryString}`, config)
            .then((res) => {
                if (res.data?.success) {
                    const data = res.data.data;
                    setDashboardData(data);
                    const dealMetrics = data?.deal_metrics || data;
                    setPaginationData({
                        totalItems: dealMetrics?.total ?? 0,
                        from: dealMetrics?.from ?? 0,
                        to: dealMetrics?.to ?? 0,
                        totalPages: dealMetrics?.last_page ?? 0,
                    });
                }
            })
            .catch((err) => {
                if (err.response?.data?.message &&
                    err.response.data.message !== "Too Many Attempts.") {
                    NotificationManager.error(err.response.data.message);
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchDashboardData();
    }, [currentPage, itemPerPage, advanceFilterData, activeTab]);

    const handleNext = () => {
        if (currentPage < paginationData.totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleItemPerPageSelect = (value) => {
        setItemPerPage(value);
        setCurrentPage(1);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [advanceFilterData, activeTab]);

    if (loading) return <Loader />;
    if (!dashboardData) return <div className="p-8 text-center">Failed to load market metrics</div>;

    const mm = dashboardData.market_metrics;
    const deals = dashboardData.deal_metrics?.data || [];

    const handleExport = (type) => {
        setExportType(type);
        setShowModal(true);
    };

    return (
        <div className="light-bg-L pb-3 px-4 py-4">
            <div className="light-bg-L">

                <div className="grid grid-cols-12 gap-3 mb-5">

                    {/* Card 1 — Avg Contract Price */}
                    <div className="col-span-2 bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-[190px]">
                        <div className="flex items-start justify-between gap-1">
                            <p className="body-N tracking-wider text-sm">
                                Avg Contract<br/> Price
                            </p>
                        </div>

                        <p className="head-2 dark-H mt-1 text-[28px] leading-tight">
                            {formatCurrency(mm.avg_contract_price)}
                        </p>

                        <div className="mt-auto pt-2 border-t border-gray-100 space-y-1">
                            <div className="flex justify-between">
                                <span className="body-S">Total Volume</span>
                                <span className="body-S">
                                    {formatCurrency(mm.total_contract_price)}
                                </span>
                            </div>

                            <div className="flex justify-between">
                                <span className="body-S">Deals Closed</span>
                                <span className="body-S">{mm.deal_count}</span>
                            </div>
                        </div>
                    </div>

                    {/* Card 2 — Avg Cap Rate */}
                    <div className="col-span-2 bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-[190px]">
                        <div className="flex items-start justify-between gap-1">
                            <p className="body-N text-sm">
                                Avg Contract Cap Rate
                            </p>
                        </div>

                        <p className="head-2 dark-H mt-1 text-[28px] leading-tight">
                            {formatPercent(mm.contract_cap_rate?.avg)}
                        </p>

                        <div className="mt-auto pt-2 border-t border-gray-100 space-y-1">
                            <div className="flex justify-between">
                                <span className="body-S">Range</span>
                                <span className="body-S text-right">
                                    {formatPercent(mm.contract_cap_rate?.min)} –{" "}
                                    {formatPercent(mm.contract_cap_rate?.max)}
                                </span>
                            </div>

                            <div className="flex justify-between">
                                <span className="body-S">Deals</span>
                                <span className="body-S">
                                    {mm.contract_cap_rate?.deal_count || mm.deal_count}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Card 3 — Avg % of Asking */}
                    <div className="col-span-2 bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-[190px]">
                        <div className="flex items-start justify-between gap-1">
                            <p className="body-N text-sm">
                                Avg % of Asking <br/>Price
                            </p>
                        </div>

                        <p className="head-2 dark-H mt-1 text-[28px] leading-tight">
                            {formatPercent(mm.asking_price_percentage?.avg, 1)}
                        </p>

                        <div className="mt-auto pt-2 border-t border-gray-100 space-y-1">
                            <div className="flex justify-between">
                                <span className="body-S">Range</span>

                                <span className="body-S text-right">
                                    {mm.asking_price_percentage?.min}% –{" "}
                                    {mm.asking_price_percentage?.max}%
                                </span>
                            </div>

                            <div className="flex justify-between">
                                <span className="body-S">Deals</span>

                                <span className="body-S">
                                    {mm.asking_price_percentage?.deal_count || mm.deal_count}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Card 4 — Avg Lease Term */}
                    <div className="col-span-2 bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-[190px]">
                        <div className="flex items-start justify-between gap-1">
                            <p className="body-N text-sm">
                                Avg Lease Term Remaining
                            </p>
                        </div>

                        <p className="head-2 dark-H mt-1 text-[28px] leading-tight">
                            {formatYears(mm.lease_term_remaining?.avg)}
                        </p>

                        <div className="mt-auto pt-2 border-t border-gray-100 space-y-1">
                            <div className="flex justify-between">
                                <span className="body-S">Range</span>

                                <span className="body-S text-right">
                                    {formatYears(mm.lease_term_remaining?.min)} –{" "}
                                    {formatYears(mm.lease_term_remaining?.max)}
                                </span>
                            </div>

                            <div className="flex justify-between">
                                <span className="body-S">Deals</span>

                                <span className="body-S">
                                    {mm.lease_term_remaining?.deal_count || mm.deal_count}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Lease Type Mix */}
                    <div className="col-span-4 bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-[190px]">

                        <div className="flex items-center justify-between mb-3">
                            <p className="body-N text-sm">
                                Lease Type Mix
                            </p>
                        </div>

                        <div className="flex items-center gap-3 flex-1 min-w-0">

                            {/* Donut Chart */}
                            <div className="flex justify-center items-center flex-shrink-0">
                                <div className="relative w-16 h-16">

                                    <svg
                                        viewBox="0 0 100 100"
                                        className="w-full h-full -rotate-90"
                                    >
                                        {/* Background circle */}
                                        <circle
                                            cx="50"
                                            cy="50"
                                            r="42"
                                            fill="none"
                                            stroke="#e5e7eb"
                                            strokeWidth="11"
                                        />

                                        {/* Data slices */}
                                        {leaseTypeMix
                                            .filter((item) => item.percent > 0)
                                            .map((item, index) => {

                                                const filteredItems = leaseTypeMix.filter(
                                                    (i) => i.percent > 0
                                                );

                                                const previousPercent = filteredItems
                                                    .slice(0, index)
                                                    .reduce(
                                                        (sum, i) => sum + i.percent,
                                                        0
                                                    );

                                                const circumference = 2 * Math.PI * 42;

                                                const dashArray = `${(item.percent / 100) * circumference
                                                    } ${circumference}`;

                                                const dashOffset =
                                                    -(
                                                        previousPercent / 100
                                                    ) * circumference;

                                                return (
                                                    <circle
                                                        key={item.key}
                                                        cx="50"
                                                        cy="50"
                                                        r="42"
                                                        fill="none"
                                                        stroke={item.color}
                                                        strokeWidth="11"
                                                        strokeDasharray={dashArray}
                                                        strokeDashoffset={dashOffset}
                                                        strokeLinecap="round"
                                                    />
                                                );
                                            })}
                                    </svg>
                                </div>
                            </div>

                            {/* Legend */}
                            <div className="flex-1 space-y-1 text-[11px] min-w-0">

                                {leaseTypeMix.map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between gap-2"
                                    >
                                        <div className="flex items-center gap-1.5 min-w-0">

                                            <div
                                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                style={{
                                                    backgroundColor: item.color,
                                                }}
                                            />

                                            <span className="body-N text-gray-700">
                                                {item.type}
                                            </span>
                                        </div>

                                        <span className="font-medium text-gray-900 flex-shrink-0">
                                            {item.percent}%
                                        </span>
                                    </div>
                                ))}

                                {/* Footer */}
                                <div className="pt-2 mt-2 border-t border-gray-100 flex justify-between text-xs">
                                    <span className="text-gray-500">
                                        Total Deals
                                    </span>

                                    <span className="font-medium">
                                        {mm.deal_count}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Deal Metrics Table */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <p className="head-4 dark-H">
                            Deal Metrics <span className="text-gray-500">({mm.deal_count} deals)</span>
                        </p>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowColumnModal(true)}
                                className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
                            >
                                Columns ⚙️
                            </button>
                            <div className="flex items-center cursor-pointer" onClick={() => handleExport("DealMetrics")}>
                                <img src={exportIcon} alt="" className="mr-2" />
                                <p className="text-green-H body-S">Export</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border overflow-hidden">
                        <div className="max-h-[380px] overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                            <table className="w-full min-w-full">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr className="uppercase body-N dark-M text-left bg-[#d1dbcf]">
                                        {visibleColumns.includes('property_name') && <th className="p-4">Property Name</th>}
                                        {visibleColumns.includes('lease_type') && <th className="p-4">Lease Type</th>}
                                        {visibleColumns.includes('lease_term') && <th className="p-4">Lease Term Remaining</th>}
                                        {visibleColumns.includes('noi') && <th className="p-4">NOI</th>}
                                        {visibleColumns.includes('contract_price') && <th className="p-4">Contract Price</th>}
                                        {visibleColumns.includes('cap_rate') && <th className="p-4">Contract Cap Rate</th>}
                                        {visibleColumns.includes('asking_percent') && <th className="p-4">% of Asking Price</th>}
                                    </tr>
                                </thead>

                                <tbody className="body-N dark-M divide-y">
                                    {deals.map((deal) => (
                                        <tr key={deal.id} className="hover:bg-gray-50">
                                            {visibleColumns.includes('property_name') && (
                                                <td className="p-4">
                                                    <div>
                                                        <p className="dark-H head-5">
                                                            <Link to={`/property/${deal.id}`}>
                                                                {deal.property_name}</Link></p>
                                                        <p className="text-gray-500 body-N">{deal.street_address}</p>
                                                        <p className="text-gray-500 body-N">{deal.city}, {deal.state}</p>
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.includes('lease_type') && <td className="p-4">{deal.lease_type}</td>}
                                            {visibleColumns.includes('lease_term') && <td className="p-4">{formatYears(deal.lease_term_remaining)}</td>}
                                            {visibleColumns.includes('noi') && <td className="p-4">{formatCurrency(deal.noi)}</td>}
                                            {visibleColumns.includes('contract_price') && <td className="p-4">{formatCurrency(deal.contract_price)}</td>}
                                            {visibleColumns.includes('cap_rate') && <td className="p-4">{formatPercent(deal.contract_cap_rate)}</td>}
                                            {visibleColumns.includes('asking_percent') && <td className="p-4">{formatPercent(deal.asking_price_percentage, 1)}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <ContactPagination
                            from="marketmetrics"
                            paginationData={paginationData}
                            handleNext={handleNext}
                            handlePrev={handlePrev}
                            itemPerPage={itemPerPage}
                            handleItemPerPageSelect={handleItemPerPageSelect}
                        />

                        {deals.length === 0 && (
                            <div className="p-8 text-center text-gray-500">No deals found</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showColumnModal && (
                <ColumnCustomizer
                    allColumns={allColumns}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                    storageKey="marketMetricsVisibleColumns"
                    onClose={() => setShowColumnModal(false)}
                />
            )}

            {showModal && (
                <ExportModal
                    showModal={showModal}
                    selectedData={selectedData}
                    exportCsvData={exportCsvData}
                    from={from}
                    type={exportType}
                    onCallApi={onCallApi}
                    advanceFilterData={advanceFilterData}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default MarketMetrics;